import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { propertyManagementIncomeAPI, propertyAPI } from '../../services/api';
import SuccessModal from '../Common/SuccessModal';
import ErrorModal from '../Common/ErrorModal';
import './PropertyManagementIncomeCreate.css';

const PropertyManagementIncomeEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    date: '',
    propertyId: '',
    totalUnitIncome: '',
    isPercentage: 'false',
    percentageValue: '',
    notes: '',
  });
  const [properties, setProperties] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [loadingProperties, setLoadingProperties] = useState(true);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    loadProperties();
  }, []);

  useEffect(() => {
    if (properties.length > 0) {
      loadData();
    }
  }, [properties]);

  const loadProperties = async () => {
    try {
      setLoadingProperties(true);
      const response = await propertyAPI.getAllForExport();
      const props = response.data || [];
      setProperties(props);
    } catch (err) {
      console.error('Error loading properties:', err);
    } finally {
      setLoadingProperties(false);
    }
  };

  const loadData = async () => {
    try {
      setLoadingData(true);
      const response = await propertyManagementIncomeAPI.getById(id);
      const data = response.data;
      const propertyId = data.propertyId?.toString() || '';
      setFormData({
        date: data.date ? data.date.split('T')[0] : '',
        propertyId: propertyId,
        totalUnitIncome: data.totalUnitIncome?.toString() || '',
        isPercentage: data.isPercentage ? 'true' : 'false',
        percentageValue: data.percentageValue?.toString() || '',
        notes: data.notes || '',
      });
      
    } catch (err) {
      setErrorMessage(t('propertyManagementIncome.edit.messages.loadError'));
      setShowErrorModal(true);
    } finally {
      setLoadingData(false);
    }
  };

  const handlePropertySelect = (propertyId) => {
    setFormData({
      ...formData,
      propertyId: propertyId.toString(),
    });
    if (errors.propertyId) {
      setErrors({ ...errors, propertyId: '' });
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    if (errors[name]) {
      setErrors({ ...errors, [name]: '' });
    }
  };

  const handleTypeChange = (e) => {
    const isPercentage = e.target.value === 'true';
    setFormData({
      ...formData,
      isPercentage: e.target.value,
      percentageValue: isPercentage ? formData.percentageValue : '',
    });
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.date.trim()) {
      newErrors.date = t('propertyManagementIncome.create.errors.dateRequired');
    }
    if (!formData.propertyId || formData.propertyId === '') {
      newErrors.propertyId = t('propertyManagementIncome.create.errors.propertyRequired');
    }
    if (!formData.totalUnitIncome || parseFloat(formData.totalUnitIncome) <= 0) {
      newErrors.totalUnitIncome = t('propertyManagementIncome.create.errors.totalUnitIncomeRequired');
    }
    if (formData.isPercentage === 'true' && (!formData.percentageValue || parseFloat(formData.percentageValue) <= 0 || parseFloat(formData.percentageValue) > 100)) {
      newErrors.percentageValue = t('propertyManagementIncome.create.errors.percentageValueRequired');
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      return;
    }

    setLoading(true);
    try {
      const submitData = {
        id: parseInt(id),
        date: formData.date,
        propertyId: parseInt(formData.propertyId),
        totalUnitIncome: parseFloat(formData.totalUnitIncome),
        isPercentage: formData.isPercentage === 'true',
        percentageValue: formData.isPercentage === 'true' && formData.percentageValue ? parseFloat(formData.percentageValue) : null,
        notes: formData.notes || null,
      };
      await propertyManagementIncomeAPI.update(id, submitData);
      setShowSuccessModal(true);
      setTimeout(() => {
        navigate('/property-management-income');
      }, 1000);
    } catch (err) {
      if (err.response?.data?.errors) {
        setErrors(err.response.data.errors);
      } else {
        setErrorMessage(t('propertyManagementIncome.edit.messages.updateError'));
        setShowErrorModal(true);
      }
      setLoading(false);
    }
  };

  if (loadingData) {
    return (
      <div className="page-container">
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">{t('common.loading')}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="form-container">
        <form onSubmit={handleSubmit}>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-calendar"></i> {t('propertyManagementIncome.create.fields.date')} *
              </label>
              <div className="input-wrapper">
                <i className="fas fa-calendar input-icon"></i>
                <input
                  type="date"
                  className={`form-input ${errors.date ? 'is-invalid' : ''}`}
                  name="date"
                  value={formData.date}
                  onChange={handleChange}
                  required
                />
              </div>
              {errors.date && (
                <div className="error-message">{errors.date}</div>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-building"></i> {t('propertyManagementIncome.create.fields.propertyName')} *
              </label>
              <PropertySearchableSelect
                properties={properties}
                value={formData.propertyId}
                onChange={handlePropertySelect}
                placeholder={t('propertyManagementIncome.create.placeholders.selectProperty')}
                disabled={loadingProperties}
                error={errors.propertyId}
              />
              {errors.propertyId && (
                <div className="error-message">{errors.propertyId}</div>
              )}
          </div>

            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-dollar-sign"></i> {t('propertyManagementIncome.create.fields.totalUnitIncome')} *
              </label>
              <div className="input-wrapper">
                <i className="fas fa-dollar-sign input-icon"></i>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  className={`form-input ${errors.totalUnitIncome ? 'is-invalid' : ''}`}
                  name="totalUnitIncome"
                  value={formData.totalUnitIncome}
                  onChange={handleChange}
                  placeholder={t('propertyManagementIncome.create.placeholders.totalUnitIncome')}
                  required
                />
              </div>
              {errors.totalUnitIncome && (
                <div className="error-message">{errors.totalUnitIncome}</div>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-percent"></i> {t('propertyManagementIncome.create.fields.type')} *
              </label>
              <div className="input-wrapper">
                <i className="fas fa-percent input-icon"></i>
                <select
                  className="form-input"
                  name="isPercentage"
                  value={formData.isPercentage}
                  onChange={handleTypeChange}
                  required
                >
                  <option value="false">{t('propertyManagementIncome.create.fields.typeFixed')}</option>
                  <option value="true">{t('propertyManagementIncome.create.fields.typePercentage')}</option>
                </select>
              </div>
            </div>
          </div>

          {formData.isPercentage === 'true' && (
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">
                  <i className="fas fa-percent"></i> {t('propertyManagementIncome.create.fields.percentageValue')} *
                </label>
                <div className="input-wrapper">
                  <i className="fas fa-percent input-icon"></i>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    max="100"
                    className={`form-input ${errors.percentageValue ? 'is-invalid' : ''}`}
                    name="percentageValue"
                    value={formData.percentageValue}
                    onChange={handleChange}
                    placeholder={t('propertyManagementIncome.create.placeholders.percentageValue')}
                    required={formData.isPercentage === 'true'}
                  />
                </div>
                {errors.percentageValue && (
                  <div className="error-message">{errors.percentageValue}</div>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">
                  <i className="fas fa-sticky-note"></i> {t('propertyManagementIncome.create.fields.notes')}
                </label>
                <div className="input-wrapper">
                  <i className="fas fa-sticky-note input-icon"></i>
                  <input
                    type="text"
                    className="form-input"
                    name="notes"
                    value={formData.notes}
                    onChange={handleChange}
                    placeholder={t('propertyManagementIncome.create.placeholders.notes')}
                    maxLength={500}
                  />
                </div>
              </div>
            </div>
          )}

          {formData.isPercentage !== 'true' && (
          <div className="form-row">
              <div className="form-group">
              <label className="form-label">
                <i className="fas fa-sticky-note"></i> {t('propertyManagementIncome.create.fields.notes')}
              </label>
              <div className="input-wrapper">
                <i className="fas fa-sticky-note input-icon"></i>
                  <input
                    type="text"
                  className="form-input"
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  placeholder={t('propertyManagementIncome.create.placeholders.notes')}
                  maxLength={500}
                />
              </div>
            </div>
          </div>
          )}

          <div className="form-actions">
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? t('propertyManagementIncome.edit.updating') : t('propertyManagementIncome.edit.save')}
            </button>
            <button type="button" className="btn btn-secondary" onClick={() => navigate('/property-management-income')}>
              {t('common.cancel')}
            </button>
          </div>
        </form>
      </div>

      <ErrorModal
        show={showErrorModal}
        message={errorMessage}
        onClose={() => setShowErrorModal(false)}
      />

      <SuccessModal
        show={showSuccessModal}
        message={t('propertyManagementIncome.edit.messages.updateSuccess')}
        onClose={() => setShowSuccessModal(false)}
      />
    </div>
  );
};

// Property Searchable Select Component
const PropertySearchableSelect = ({ properties, value, onChange, placeholder, disabled, error }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const { t } = useTranslation();
  const selectRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (selectRef.current && !selectRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectedProperty = properties.find(p => String(p.id) === String(value));
  
  const filteredProperties = properties.filter(property => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    const nameEng = (property.nameEng || '').toLowerCase();
    const nameAr = (property.nameAr || '').toLowerCase();
    const code = (property.code || '').toLowerCase();
    return nameEng.includes(search) || nameAr.includes(search) || code.includes(search);
  });

  return (
    <div className={`searchable-select ${error ? 'is-invalid' : ''}`} ref={selectRef}>
      <div 
        className="searchable-select-input"
        onClick={() => !disabled && setIsOpen(!isOpen)}
      >
        <i className="fas fa-building input-icon"></i>
        <input
          type="text"
          readOnly
          value={selectedProperty ? (selectedProperty.nameEng || selectedProperty.nameAr || '') : ''}
          placeholder={placeholder}
          className={`form-input ${error ? 'is-invalid' : ''}`}
          disabled={disabled}
        />
        <i className={`fas fa-chevron-${isOpen ? 'up' : 'down'} searchable-select-chevron`}></i>
      </div>
      {isOpen && !disabled && (
        <div className="searchable-select-dropdown">
          <div className="searchable-select-search">
            <i className="fas fa-search"></i>
            <input
              type="text"
              placeholder={t('common.search')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              autoFocus
            />
          </div>
          <div className="searchable-select-options">
            {filteredProperties.length === 0 ? (
              <div className="searchable-select-no-results">{t('common.noData')}</div>
            ) : (
              filteredProperties.map(property => (
                <div
                  key={property.id}
                  className={`searchable-select-option ${String(property.id) === String(value) ? 'selected' : ''}`}
                  onClick={() => {
                    onChange(property.id);
                    setIsOpen(false);
                    setSearchTerm('');
                  }}
                >
                  {property.nameEng || property.nameAr} 
                  {property.code && ` (${property.code})`}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PropertyManagementIncomeEdit;


